import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Badge, statusVariant } from "../components/Badge";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Monitor, Smartphone, Upload, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";

const EMPTY = { title: "", image: "", image_mobile: "", link: "", order: 0, status: "active", page: "both" };
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const PAGE_LABEL = { login: "Login only", register: "Sign up only", both: "Login + Sign up" };

export default function AuthBanners() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth-banners");
      setRows(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY, order: rows.length }); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      title: row.title || "",
      image: row.image || "",
      image_mobile: row.image_mobile || "",
      link: row.link || "",
      order: typeof row.order === "number" ? row.order : 0,
      status: row.status || "active",
      page: ["login", "register", "both"].includes(row.page) ? row.page : "both",
    });
    setOpen(true);
  };
  const close = () => { if (saving) return; setOpen(false); setEditing(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image) { toast.error("Please add a desktop image"); return; }
    setSaving(true);
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing) { await api.patch(`/auth-banners/${editing.id}`, payload); toast.success("Banner updated"); }
      else { await api.post("/auth-banners", payload); toast.success("Banner created"); }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editing ? "Update failed" : "Create failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title || "banner"}"?`)) return;
    await api.delete(`/auth-banners/${row.id}`);
    toast.success("Deleted");
    load();
  };

  const toggleStatus = async (row) => {
    const next = row.status === "active" ? "inactive" : "active";
    await api.patch(`/auth-banners/${row.id}`, { status: next });
    load();
  };

  const move = async (row, dir) => {
    const sorted = [...rows].sort((a, b) => a.order - b.order);
    const i = sorted.findIndex((r) => r.id === row.id);
    const j = i + dir;
    if (j < 0 || j >= sorted.length) return;
    const a = sorted[i], b = sorted[j];
    await Promise.all([
      api.patch(`/auth-banners/${a.id}`, { order: b.order }),
      api.patch(`/auth-banners/${b.id}`, { order: a.order }),
    ]);
    load();
  };

  const sorted = [...rows].sort((a, b) => a.order - b.order);

  return (
    <div data-testid="auth-banners-page">
      <PageHeader
        title="Login Banners"
        subtitle="Promo banners for the Login / Register pages. Choose whether each banner shows on Login, Sign up, or both. Full image is always displayed (never cropped) over a blurred backdrop. Add a mobile image for the best phone view. Reorder with the arrows; only Active banners appear on the site."
        actions={(
          <button onClick={openCreate} className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-1.5" data-testid="auth-banners-add-btn">
            <Plus size={14} /> Add Banner
          </button>
        )}
      />

      {open && (
        <BannerForm form={form} setForm={setForm} onSubmit={submit} onClose={close} saving={saving} editing={!!editing} />
      )}

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>
      ) : sorted.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">No login banners yet. Click “Add Banner”.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="auth-banners-grid">
          {sorted.map((r, i) => (
            <div key={r.id} className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={`auth-banner-card-${r.id}`}>
              <div className="relative h-40 bg-[#07100a] overflow-hidden">
                {r.image ? (
                  <>
                    <img src={r.image} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-60" />
                    <img src={r.image} alt={r.title} className="relative z-[1] w-full h-full object-contain" />
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-amber-400 text-xs">No image</div>
                )}
              </div>
              <div className="p-4 flex items-center justify-between flex-wrap gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{r.title || <span className="text-gray-400">Untitled</span>}</div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 flex-wrap">
                    <button onClick={() => toggleStatus(r)} title="Toggle active" data-testid={`auth-banner-toggle-${r.id}`}>
                      <Badge variant={statusVariant(r.status)}>{r.status}</Badge>
                    </button>
                    <span className="inline-flex items-center gap-1 h-6 px-1.5 border border-indigo-200 bg-indigo-50 text-indigo-700 rounded-sm" data-testid={`auth-banner-page-${r.id}`}>
                      {PAGE_LABEL[r.page] || PAGE_LABEL.both}
                    </span>
                    {r.image_mobile && (
                      <span className="inline-flex items-center gap-1 h-6 px-1.5 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-sm">
                        <Smartphone size={10} /> mobile
                      </span>
                    )}
                    <span>Order · {r.order}</span>
                    {r.link && (
                      <a href={r.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                        <ExternalLink size={11} /> link
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(r, -1)} disabled={i === 0} className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:bg-gray-50 disabled:opacity-30" title="Move up" data-testid={`auth-banner-up-${r.id}`}>
                    <ArrowUp size={14} />
                  </button>
                  <button onClick={() => move(r, 1)} disabled={i === sorted.length - 1} className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:bg-gray-50 disabled:opacity-30" title="Move down" data-testid={`auth-banner-down-${r.id}`}>
                    <ArrowDown size={14} />
                  </button>
                  <button onClick={() => openEdit(r)} data-testid={`auth-banner-edit-${r.id}`} className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-gray-200 rounded-sm text-gray-700 hover:bg-gray-50">
                    <Pencil size={12} /> Edit
                  </button>
                  <button onClick={() => remove(r)} data-testid={`auth-banner-delete-${r.id}`} className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-red-600 hover:border-red-200">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BannerForm({ form, setForm, onSubmit, onClose, saving, editing }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h3 className="font-display font-bold">{editing ? "Edit login banner" : "Add login banner"}</h3>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center"><X size={16} /></button>
        </div>

        <form onSubmit={onSubmit} className="p-5 space-y-4" data-testid="auth-banners-form">
          <Field label="Title (internal)">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Welcome Bonus" className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500" data-testid="auth-banners-input-title" />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ImageInput label="Desktop image *" icon={<Monitor size={11} />} value={form.image} onChange={(v) => setForm({ ...form, image: v })} hint="Recommended wide banner (e.g. 1280×530)" testId="auth-banners-input-image" />
            <ImageInput label="Mobile image (optional)" icon={<Smartphone size={11} />} value={form.image_mobile} onChange={(v) => setForm({ ...form, image_mobile: v })} hint="Taller/square art for phones. Leave empty to reuse desktop." testId="auth-banners-input-image-mobile" />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Show on which page?</label>
            <div className="grid grid-cols-3 gap-2" data-testid="auth-banners-page-select">
              {[
                { id: "both", label: "Login + Sign up" },
                { id: "login", label: "Login only" },
                { id: "register", label: "Sign up only" },
              ].map((opt) => {
                const active = form.page === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setForm({ ...form, page: opt.id })}
                    data-testid={`auth-banners-page-${opt.id}`}
                    className={`h-10 px-2 rounded-sm border text-sm font-semibold transition-colors ${
                      active ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-200 bg-white text-gray-600 hover:border-emerald-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Link URL (optional)">
              <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://… (opens on click)" className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500" data-testid="auth-banners-input-link" />
            </Field>
            <Field label="Order">
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500" data-testid="auth-banners-input-order" />
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="auth-banners-input-status">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>
          </div>

          {form.image && (
            <div className="border-t border-gray-200 pt-4">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Live preview</div>
              <div className="relative h-40 rounded-sm overflow-hidden bg-[#07100a]">
                <img src={form.image} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-60" />
                <img src={form.image} alt="preview" className="relative z-[1] w-full h-full object-contain" />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={saving} className="h-10 px-4 border border-gray-200 rounded-sm text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="auth-banners-submit" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white rounded-sm text-sm font-semibold">
              {saving ? "Saving…" : editing ? "Save changes" : "Create banner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImageInput({ label, icon, value, onChange, hint, testId }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/banners/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const absoluteUrl = data.url.startsWith("http") ? data.url : `${BACKEND}${data.url}`;
      onChange(absoluteUrl);
      toast.success(`Uploaded (${Math.round(data.size / 1024)} KB)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1 inline-flex items-center gap-1">{icon} {label}</label>
      <div className="flex items-stretch gap-2">
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://… or upload →" className="flex-1 min-w-0 h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500" data-testid={testId} />
        <label className={`shrink-0 inline-flex items-center justify-center gap-1 h-10 px-3 border rounded-sm text-xs font-semibold cursor-pointer transition-colors ${uploading ? "border-gray-200 bg-gray-100 text-gray-400 cursor-wait" : "border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`} data-testid={`${testId}-upload`}>
          <Upload size={13} /> {uploading ? "…" : "Upload"}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/avif" disabled={uploading} onChange={onFile} className="hidden" />
        </label>
        {value && (
          <button type="button" onClick={() => onChange("")} title="Clear" className="shrink-0 h-10 w-10 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-red-600 hover:border-red-200">
            <X size={14} />
          </button>
        )}
      </div>
      {hint && <div className="text-[10px] text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}
